import React, { useContext, useState, useEffect } from 'react';
import './CartPage.css';
import cardContext from '../../context/cards/cardContext';
import { FiTrash2, FiClock, FiCheckCircle, FiFileText } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import orderService from '../../services/api/orderService';
import authService from '../../services/api/authService';
import pdfGenerator from '../../utils/pdfGenerator';
import BillPreviewModal from './BillPreviewModal';

const CartPage = () => {
    const { getCartItems, updateQuantity, removeFromCart, clearCart } = useContext(cardContext);
    const [previousOrderGroups, setPreviousOrderGroups] = useState([]);
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);
    const [orderPlaced, setOrderPlaced] = useState(false);
    const [ordersInProcess, setOrdersInProcess] = useState([]);
    const [activeTicketCanGenerateBill, setActiveTicketCanGenerateBill] = useState(false);
    const [cookingInstructions, setCookingInstructions] = useState('');
    const [placedOrderId, setPlacedOrderId] = useState(null);
    const [isGeneratingBill, setIsGeneratingBill] = useState(false);
    const [billData, setBillData] = useState(null);
    const [showBillPreview, setShowBillPreview] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const cartItems = getCartItems();
    const totalAmount = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    
    // Calculate estimated preparation time based on cart items
    const preparationTime = cartItems.length > 0 
        ? Math.ceil(cartItems.reduce((total, item) => total + item.quantity, 0) * 8) 
        : null;

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Fetch orders when component mounts and when order is placed
    useEffect(() => {
        fetchCustomerOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderPlaced]);

    const fetchCustomerOrders = async () => {
        const customerData = authService.getCustomerData();
        const customerTicketId = customerData.customerTicketId;

        if (!customerTicketId) {
            return;
        }

        const historyResult = await orderService.getCustomerTicketHistory();
        
        if (historyResult.success && historyResult.data) {
            categorizeCustomerTicketGroups(historyResult.data, customerTicketId);
            return;
        }

        const currentTicketResult = await orderService.getOrdersByCustomerTicket(customerTicketId);
        
        if (currentTicketResult.success && currentTicketResult.data) {
            categorizeOrders(currentTicketResult.data, {
                customerTicketId,
                isCurrentTicket: true
            });
        }
    };

    const categorizeOrders = (orders, ticketInfo = {}) => {
        if (!orders || orders.length === 0) {
            setOrdersInProcess([]);
            setPreviousOrderGroups([]);
            setActiveTicketCanGenerateBill(false);
            return;
        }

        const inProcess = [];
        const completed = [];

        orders.forEach(order => {
            const orderData = {
                id: order.id,
                orderTime: formatOrderTime(order.createdAt),
                items: (order.items || []).map(formatOrderItem),
                status: order.status,
                total: calculateOrderTotal(order.items || []),
                estimatedDelivery: estimateDeliveryTime(order)
            };

            if (order.status === 'RESOLVED') {
                completed.push(orderData);
            } else {
                inProcess.push(orderData);
            }
        });

        setOrdersInProcess(inProcess);
        setActiveTicketCanGenerateBill(ticketInfo.isCurrentTicket && inProcess.length === 0 && completed.length > 0);
        setPreviousOrderGroups(completed.length > 0 ? [{
            customerTicketId: ticketInfo.customerTicketId,
            title: ticketInfo.isCurrentTicket ? 'Current Visit' : 'Previous Visit',
            subtitle: `${completed.length} completed order${completed.length === 1 ? '' : 's'}`,
            orders: completed
        }] : []);
    };

    const categorizeCustomerTicketGroups = (historyData, currentCustomerTicketId) => {
        const ticketGroups = normalizeCustomerTicketGroups(historyData);
        const inProcess = [];
        const completedGroups = [];
        let currentTicketCanGenerateBill = false;

        ticketGroups.forEach(ticketGroup => {
            const ticketId = String(ticketGroup.customerTicketId || ticketGroup.id || '');
            const isCurrentTicket = ticketId === String(currentCustomerTicketId);
            const completed = [];

            (ticketGroup.orders || []).forEach(order => {
                const orderData = {
                    id: order.id,
                    orderTime: formatOrderTime(order.createdAt),
                    items: (order.items || []).map(formatOrderItem),
                    status: order.status,
                    total: calculateOrderTotal(order.items || []),
                    estimatedDelivery: estimateDeliveryTime(order)
                };

                if (order.status === 'RESOLVED') {
                    completed.push(orderData);
                } else if (isCurrentTicket) {
                    inProcess.push(orderData);
                }
            });

            if (completed.length > 0) {
                completedGroups.push({
                    customerTicketId: ticketId,
                    title: isCurrentTicket ? 'Current Visit' : getTicketGroupTitle(ticketGroup),
                    subtitle: buildTicketSubtitle(ticketGroup, completed.length),
                    orders: completed
                });
            }

            if (isCurrentTicket) {
                const billAlreadyGenerated = Boolean(ticketGroup.totalBill || ticketGroup.billDetails);
                const ticketClosed = ticketGroup.status === 'RESOLVED' || ticketGroup.status === 'CLOSED';
                currentTicketCanGenerateBill = inProcess.length === 0
                    && completed.length > 0
                    && !billAlreadyGenerated
                    && !ticketClosed;
            }
        });

        setOrdersInProcess(inProcess);
        setActiveTicketCanGenerateBill(currentTicketCanGenerateBill);
        setPreviousOrderGroups(completedGroups);
    };

    const normalizeCustomerTicketGroups = (historyData) => {
        if (Array.isArray(historyData)) {
            return historyData;
        }

        if (Array.isArray(historyData.customerTickets)) {
            return historyData.customerTickets;
        }

        if (Array.isArray(historyData.tickets)) {
            return historyData.tickets;
        }

        return [];
    };

    const formatOrderTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
        });
    };

    const formatTicketDate = (timestamp) => {
        if (!timestamp) {
            return 'Previous Visit';
        }

        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getTicketGroupTitle = (ticketGroup) => {
        const timestamp = ticketGroup.createdAt || ticketGroup.openedAt;
        return timestamp ? `Visit ${formatTicketDate(timestamp)}` : 'Previous Visit';
    };

    const buildTicketSubtitle = (ticketGroup, orderCount) => {
        const parts = [
            `${orderCount} completed order${orderCount === 1 ? '' : 's'}`
        ];

        if (ticketGroup.tableName || ticketGroup.tableNumber) {
            parts.push(`Table ${ticketGroup.tableName || ticketGroup.tableNumber}`);
        }

        return parts.join(' • ');
    };

    const calculateOrderTotal = (items) => {
        return items.reduce((total, item) => {
            const price = item.menuItemPrice ?? item.price ?? 0;
            const quantity = item.quantityOrdered ?? item.quantity ?? 0;
            return total + (price * quantity);
        }, 0);
    };

    const formatOrderItem = (item) => {
        const name = item.menuItemName || item.name || item.itemName || 'Menu item';
        const quantity = item.quantityOrdered ?? item.quantity;

        return quantity ? `${name} x ${quantity}` : name;
    };

    const estimateDeliveryTime = (order) => {
        // Calculate estimated delivery based on order status and items
        const itemCount = (order.items || []).reduce((count, item) => count + (item.quantityOrdered ?? item.quantity ?? 0), 0);
        
        if (order.status === 'OPEN') {
            return Math.ceil(itemCount * 8 + 5); // 8-13 minutes per item for new orders
        } else if (order.status === 'IN_PROGRESS') {
            return Math.ceil(itemCount * 3 + 2); // 5-10 minutes for in-progress
        }
        
        return 0; // Resolved orders
    };

    const getStatusLabel = (status) => {
        const statusMap = {
            'OPEN': 'Preparing',
            'IN_PROGRESS': 'Almost Ready',
            'RESOLVED': 'Delivered'
        };
        return statusMap[status] || status;
    };

    const handleQuantityUpdate = (cardId, newQuantity) => {
        if (newQuantity === 0) {
            removeFromCart(cardId);
        } else {
            updateQuantity(cardId, newQuantity);
        }
    };

    const handlePlaceOrder = async () => {
        setIsPlacingOrder(true);
        setError('');
        
        const customerData = authService.getCustomerData();
        const customerTicketId = customerData.customerTicketId;

        if (!customerTicketId) {
            setError('Customer ticket not found. Please refresh and try again.');
            setIsPlacingOrder(false);
            return;
        }

        const items = orderService.formatCartItemsForOrder(cartItems, cookingInstructions);
        
        const result = await orderService.placeOrder(customerTicketId, items);
        
        if (result.success) {
            const activeCustomerTicketId = result.data?.customerTicketId;
            if (activeCustomerTicketId) {
                orderService.setCustomerTicketId(activeCustomerTicketId);
            }

            setIsPlacingOrder(false);
            setOrderPlaced(true);
            setPlacedOrderId(result.data.id);
            
            // Clear cart after successful order
            clearCart();
            
            // Clear cooking instructions
            setCookingInstructions('');
            
            // Fetch updated orders
            fetchCustomerOrders();
        } else {
            setIsPlacingOrder(false);
            setError(result.error || 'Failed to place order. Please try again.');
        }
    };

    const handleGenerateBill = async () => {
        setIsGeneratingBill(true);
        setError('');

        const customerData = authService.getCustomerData();
        const customerTicketId = customerData.customerTicketId;

        if (!customerTicketId) {
            setError('Customer ticket not found. Please refresh and try again.');
            setIsGeneratingBill(false);
            return;
        }

        const result = await orderService.generateBill(customerTicketId);
        
        if (result.success) {
            setBillData(result.data);
            setShowBillPreview(true);
            setActiveTicketCanGenerateBill(false);
            fetchCustomerOrders();
            setIsGeneratingBill(false);
        } else {
            setError(result.error || 'Failed to generate bill. Please try again.');
            setIsGeneratingBill(false);
        }
    };

    const handleDownloadBill = () => {
        if (!billData) return;

        const customerData = authService.getCustomerData();
        
        try {
            pdfGenerator.downloadCustomerBill(billData, {
                name: customerData.name,
                phoneNumber: customerData.phoneNumber,
                email: customerData.email
            });
            
            // Close the preview modal after successful download
            setTimeout(() => {
                setShowBillPreview(false);
            }, 500);
        } catch (pdfError) {
            console.error('PDF generation error:', pdfError);
            setError('Failed to download PDF. Please try again.');
        }
    };

    return (
        <div className="cart-page">
            <div className="cart-header">
                <button className="back-button" onClick={() => navigate('/')}>
                    ← Back to Menu
                </button>
                <h1>Your Cart</h1>
            </div>

            {cartItems.length === 0 ? (
                <div className="empty-cart">
                    <h2>Your cart is empty</h2>
                    <p>Add some delicious items from our menu!</p>
                    <button className="browse-menu-btn" onClick={() => navigate('/')}>
                        Browse Menu
                    </button>
                </div>
            ) : (
                <>
                    {/* Order Placed Success Message */}
                    {orderPlaced && placedOrderId && (
                        <div className="order-success-section" style={{
                            backgroundColor: '#e8f5e9',
                            padding: '20px',
                            borderRadius: '12px',
                            marginBottom: '20px',
                            border: '2px solid #4caf50'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                <FiCheckCircle size={32} color="#4caf50" />
                                <div>
                                    <h2 style={{ margin: 0, color: '#2e7d32' }}>Order Placed Successfully!</h2>
                                    <p style={{ margin: '4px 0 0 0', color: '#558b2f' }}>Order ID: {placedOrderId}</p>
                                </div>
                            </div>
                            <p style={{ margin: '12px 0 0 0', color: '#558b2f' }}>
                                Your order is being prepared. You can continue browsing the menu or generate your bill when ready.
                            </p>
                            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                                <button 
                                    className="browse-menu-btn" 
                                    onClick={() => navigate('/')}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#4caf50',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        flex: 1
                                    }}
                                >
                                    Browse Menu
                                </button>
                                
                            </div>
                        </div>
                    )}

                    {/* Current Cart Items */}
                    {!orderPlaced && (
                    <div className="current-cart-section">
                        <h2>Current Order</h2>
                        <div className="cart-items">
                            {cartItems.map((item) => (
                                <div key={item.id} className="cart-item">
                                    <img src={item.image} alt={item.name} className="cart-item-image" />
                                    <div className="cart-item-details">
                                        <h3>{item.name}</h3>
                                        <p className="cart-item-price">₹{item.price} each</p>
                                        <p className="cart-item-total">Total: ₹{item.price * item.quantity}</p>
                                    </div>
                                    <div className="quantity-controls">
                                        <button 
                                            className="quantity-btn decrease"
                                            onClick={() => handleQuantityUpdate(item.id, item.quantity - 1)}
                                        >
                                            -
                                        </button>
                                        <span className="quantity-display">{item.quantity}</span>
                                        <button 
                                            className="quantity-btn increase"
                                            onClick={() => handleQuantityUpdate(item.id, item.quantity + 1)}
                                        >
                                            +
                                        </button>
                                    </div>
                                    <button 
                                        className="remove-item-btn"
                                        onClick={() => removeFromCart(item.id)}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Cooking Instructions */}
                        <div className="cooking-instructions-section">
                            <h3>Special Cooking Instructions</h3>
                            <textarea
                                className="cooking-instructions-input"
                                placeholder="Any special cooking preferences? (e.g., extra spicy, less oil, no onions, etc.)"
                                value={cookingInstructions}
                                onChange={(e) => setCookingInstructions(e.target.value)}
                                rows={3}
                                maxLength={200}
                            />
                            <div className="character-count">
                                {cookingInstructions.length}/200 characters
                            </div>
                        </div>

                        {/* Order Summary */}
                        <div className="order-summary">
                            <div className="summary-row">
                                <span>Total Items:</span>
                                <span>{cartItems.reduce((total, item) => total + item.quantity, 0)}</span>
                            </div>
                            <div className="summary-row total">
                                <span>Total Amount:</span>
                                <span>₹{totalAmount}</span>
                            </div>
                            {preparationTime && (
                                <div className="preparation-time">
                                    <FiClock className="clock-icon" />
                                    <span>Estimated preparation time: {preparationTime} minutes</span>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="action-buttons">
                            {/* Error Message */}
                            {error && (
                                <div className="error-message" style={{
                                    color: '#d32f2f',
                                    backgroundColor: '#ffebee',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    marginBottom: '16px',
                                    fontSize: '14px'
                                }}>
                                    {error}
                                </div>
                            )}

                            {/* Place Order Button */}
                            <button 
                                className={`place-order-btn ${isPlacingOrder ? 'loading' : ''} ${orderPlaced ? 'success' : ''}`}
                                onClick={handlePlaceOrder}
                                disabled={isPlacingOrder || orderPlaced}
                            >
                                {orderPlaced ? (
                                    <>
                                        <FiCheckCircle /> Order Placed Successfully!
                                    </>
                                ) : isPlacingOrder ? (
                                    'Placing Order...'
                                ) : (
                                    `Place Order - ₹${totalAmount}`
                                )}
                            </button>
                        </div>
                    </div>
                    )}
                </>
            )}

            {/* Generate Bill Section - Shows only when all orders are resolved and user has completed orders */}
            {cartItems.length === 0 && activeTicketCanGenerateBill && (
                <div className="generate-bill-section" style={{
                    backgroundColor: '#e3f2fd',
                    padding: '20px',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    border: '2px solid #2196f3',
                    textAlign: 'center'
                }}>
                    <h3 style={{ margin: '0 0 12px 0', color: '#1565c0' }}>
                        <FiFileText style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                        Ready to Check Out?
                    </h3>
                    <p style={{ margin: '0 0 16px 0', color: '#1976d2', fontSize: '14px' }}>
                        All your orders are complete! Generate your final bill to checkout.
                    </p>
                    {error && (
                        <div style={{
                            color: '#d32f2f',
                            backgroundColor: '#ffebee',
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '12px',
                            fontSize: '14px'
                        }}>
                            {error}
                        </div>
                    )}
                    <button 
                        onClick={handleGenerateBill}
                        disabled={isGeneratingBill}
                        style={{
                            padding: '12px 32px',
                            backgroundColor: isGeneratingBill ? '#90caf9' : '#2196f3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: isGeneratingBill ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                            if (!isGeneratingBill) {
                                e.target.style.backgroundColor = '#1976d2';
                                e.target.style.transform = 'translateY(-2px)';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isGeneratingBill) {
                                e.target.style.backgroundColor = '#2196f3';
                                e.target.style.transform = 'translateY(0)';
                            }
                        }}
                    >
                        <FiFileText /> {isGeneratingBill ? 'Generating Bill...' : 'Generate Bill & Checkout'}
                    </button>
                </div>
            )}

            {/* Info message when orders are still in process */}
            {ordersInProcess.length > 0 && (
                <div className="info-message-section" style={{
                    backgroundColor: '#fff3cd',
                    padding: '16px 20px',
                    borderRadius: '12px',
                    marginBottom: '20px',
                    border: '2px solid #ffc107',
                    textAlign: 'center'
                }}>
                    <p style={{ margin: 0, color: '#856404', fontSize: '14px', fontWeight: '500' }}>
                        ⏳ Your orders are being prepared. The bill will be available once all orders are delivered.
                    </p>
                </div>
            )}

            {/* Orders in Process Section */}
            {ordersInProcess.length > 0 && (
                <div className="orders-in-process-section">
                    <h2>Orders in Process</h2>
                    <div className="orders-in-process">
                        {ordersInProcess.map((order) => (
                            <div key={order.id} className="process-order">
                                <div className="order-header">
                                    <span className="order-time">Ordered at {order.orderTime}</span>
                                    <span className={`order-status ${getStatusLabel(order.status).toLowerCase().replace(' ', '-')}`}>
                                        {getStatusLabel(order.status)}
                                    </span>
                                </div>
                                <div className="order-items">
                                    {order.items.join(', ')}
                                </div>
                                {order.estimatedDelivery > 0 && (
                                    <div className="delivery-estimate">
                                        <FiClock className="clock-icon" />
                                        <span>Arriving in ~{order.estimatedDelivery} minutes</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Previous Orders Section */}
            {previousOrderGroups.length > 0 && (
                <div className="previous-orders-section">
                    <h2>Your Previous Orders</h2>
                    <div className="previous-ticket-groups">
                        {previousOrderGroups.map((ticketGroup) => (
                            <div key={ticketGroup.customerTicketId || ticketGroup.title} className="previous-ticket-group">
                                <div className="ticket-group-header">
                                    <div>
                                        <h3>{ticketGroup.title}</h3>
                                        <p>{ticketGroup.subtitle}</p>
                                    </div>
                                    {ticketGroup.customerTicketId && (
                                        <span className="previous-ticket-id">Ticket #{ticketGroup.customerTicketId}</span>
                                    )}
                                </div>

                                <div className="previous-orders">
                                    {ticketGroup.orders.map((order) => (
                                        <div key={order.id} className="previous-order">
                                            <div className="order-header">
                                                <span className="order-time">{order.orderTime}</span>
                                                <span className={`order-status ${getStatusLabel(order.status).toLowerCase()}`}>
                                                    {getStatusLabel(order.status)}
                                                </span>
                                            </div>
                                            <div className="order-items">
                                                {order.items.join(', ')}
                                            </div>
                                            <div className="order-total">₹{order.total.toFixed(2)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Bill Preview Modal */}
            {showBillPreview && billData && (
                <BillPreviewModal
                    billData={billData}
                    customerInfo={authService.getCustomerData()}
                    onClose={() => setShowBillPreview(false)}
                    onDownload={handleDownloadBill}
                    isDownloading={false}
                />
            )}
        </div>
    );
};

export default CartPage;
